import React, { useState, useEffect } from 'react';

interface Venue {
  name: string;
  address: string;
}

interface VenueSelectorProps {
  selectedVenue: string;
  selectedAddress: string;
  onVenueChange: (venue: string, address: string) => void;
}

const VenueSelector: React.FC<VenueSelectorProps> = ({
  selectedVenue,
  selectedAddress,
  onVenueChange
}) => {
  // Predefined venue list
  const predefinedVenues: Venue[] = [
    {
      name: "Venue Cymru, Llandudno",
      address: "The Promenade, Penrhyn Cres, Llandudno LL30 1BB"
    },
    {
      name: "Swansea Arena",
      address: "Oystermouth Rd, Maritime Quarter, Bae Copr Bay, Swansea SA1 3BX"
    },
    {
      name: "Edinburgh EICC",
      address: "Edinburgh International Conference Centre, 150 Morrison St, Edinburgh EH3 8EE"
    },
    {
      name: "Marriot Hotel, Bristol",
      address: "College Green, Bristol BS1 5TA"
    },
    {
      name: "ACC Liverpool",
      address: "King's Dock, Port of Liverpool, Kings Dock St, Liverpool L3 4FP"
    },
    {
      name: "Double Tree by Hilton Metropole, Brighton",
      address: "Kings Rd, Brighton and Hove, Brighton BN1 2FU"
    },
    {
      name: "Bournemouth International Centre",
      address: "International Centre, Exeter Rd, Bournemouth BH2 5BH"
    },
    {
      name: "SEC Glasgow",
      address: "Exhibition Way, Glasgow G3 8YW"
    }
  ];

  // Find if the current venue is in our predefined list
  const findPredefinedVenue = () => {
    const venue = predefinedVenues.find(v => v.name === selectedVenue);
    return venue || null;
  };

  // Determine initial selection state
  const initialSelectedVenue = findPredefinedVenue();
  
  // If we have a venue and address but it's not in our predefined list, it must be custom
  const initialIsCustom = selectedVenue !== '' && !initialSelectedVenue;

  // Current selected venue value - will be 'custom' or the name of a predefined venue
  const [selectedVenueValue, setSelectedVenueValue] = useState<string>(
    initialSelectedVenue ? initialSelectedVenue.name : (initialIsCustom ? 'custom' : '')
  );
  
  // States for custom venue details
  const [customVenueName, setCustomVenueName] = useState<string>(
    initialIsCustom ? selectedVenue : ''
  );
  
  const [customVenueAddress, setCustomVenueAddress] = useState<string>(
    initialIsCustom ? selectedAddress : ''
  );

  // Handle venue selection change
  const handleVenueSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedVenueValue(value);
    
    if (value === 'custom') {
      // User selected custom option
      onVenueChange(customVenueName, customVenueAddress);
    } else if (value !== '') {
      // User selected a predefined venue
      const selectedVenue = predefinedVenues.find(v => v.name === value);
      if (selectedVenue) {
        onVenueChange(selectedVenue.name, selectedVenue.address);
      }
    } else {
      // Empty selection (shouldn't happen in regular use)
      onVenueChange('', '');
    }
  };

  // Handle custom venue name change
  const handleCustomVenueNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setCustomVenueName(name);
    onVenueChange(name, customVenueAddress);
  };

  // Handle custom venue address change
  const handleCustomVenueAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value;
    setCustomVenueAddress(address);
    onVenueChange(customVenueName, address);
  };

  return (
    <div className="venue-selector">
      <div className="form-field">
        <label>Venue</label>
        <select
          className="venue-select"
          value={selectedVenueValue}
          onChange={handleVenueSelectionChange}
        >
          <option value="">Select venue</option>
          <option value="custom">Select Custom</option>
          {predefinedVenues.map((venue, index) => (
            <option key={index} value={venue.name}>
              {venue.name}
            </option>
          ))}
        </select>
      </div>
      
      {selectedVenueValue === 'custom' ? (
        // Custom venue fields
        <div className="custom-venue-fields">
          <div className="form-field">
            <label>Venue Name</label>
            <input
              type="text"
              value={customVenueName}
              onChange={handleCustomVenueNameChange}
              placeholder="Enter venue name"
            />
          </div>
          
          <div className="form-field">
            <label>Venue Address</label>
            <input
              type="text"
              value={customVenueAddress}
              onChange={handleCustomVenueAddressChange}
              placeholder="Enter venue address"
            />
          </div>
        </div>
      ) : selectedVenueValue !== '' ? (
        // Predefined venue display
        <div className="predefined-venue-fields">
          <div className="form-field">
            <label>Venue Name</label>
            <input
              type="text"
              value={selectedVenueValue}
              className="readonly-field"
              readOnly
            />
          </div>
          
          <div className="form-field">
            <label>Venue Address</label>
            <input
              type="text"
              value={predefinedVenues.find(v => v.name === selectedVenueValue)?.address || ''}
              className="readonly-field"
              readOnly
            />
            <small className="helper-text">Address auto-populated from selected venue</small>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default VenueSelector;